/**
 * Terminal Spawning Utilities
 *
 * Detect and spawn new terminal windows for wizard UI.
 */

import { spawn } from 'child_process';
import * as fs from 'fs-extra';
import * as path from 'path';
import * as os from 'os';

/**
 * Validate that a path is safe (no traversal, within expected dirs)
 */
export function isValidPath(filePath: string, allowedDirs: string[]): boolean {
  const resolved = path.resolve(filePath);
  const normalized = path.normalize(resolved);

  // Check for path traversal attempts
  if (normalized.includes('..')) {
    return false;
  }

  // Check if path is within allowed directories
  return allowedDirs.some(dir => normalized.startsWith(path.resolve(dir)));
}

/**
 * Sanitize argument for shell safety - reject dangerous characters
 */
function sanitizeArg(arg: string): string {
  // Allow safe characters: word chars, hyphens, dots, slashes, equals, and @ (for scoped npm packages)
  if (!/^[\w\-\.\/=@]+$/.test(arg)) {
    throw new Error(`Invalid argument contains unsafe characters: ${arg.substring(0, 20)}...`);
  }
  return arg;
}

/**
 * Terminal emulators to try, in order of preference
 * Each entry specifies how to run a command in that terminal
 */
const TERMINAL_COMMANDS = [
  // Linux GUI terminals
  { cmd: 'gnome-terminal', args: ['--wait', '--'], wrapInBash: false },
  { cmd: 'konsole', args: ['--hold', '-e'], wrapInBash: false },
  { cmd: 'xfce4-terminal', args: ['--hold', '-e'], wrapInBash: true },
  { cmd: 'xterm', args: ['-hold', '-e'], wrapInBash: false },
  { cmd: 'x-terminal-emulator', args: ['-e'], wrapInBash: true },
  // macOS
  { cmd: 'open', args: ['-a', 'Terminal'], wrapInBash: false },
];

/**
 * Check if a command exists in PATH
 */
async function commandExists(cmd: string): Promise<boolean> {
  return new Promise((resolve) => {
    const which = spawn('which', [cmd]);
    which.on('close', (code) => resolve(code === 0));
    which.on('error', () => resolve(false));
  });
}

/**
 * Find available terminal emulator
 */
export async function findTerminal(): Promise<{ cmd: string; args: string[]; wrapInBash: boolean } | null> {
  for (const terminal of TERMINAL_COMMANDS) {
    if (await commandExists(terminal.cmd)) {
      return terminal;
    }
  }
  return null;
}

/**
 * Check if we're running in a headless environment
 */
export function isHeadless(): boolean {
  // No display available
  if (!process.env.DISPLAY && !process.env.WAYLAND_DISPLAY) {
    return true;
  }
  // SSH session without X forwarding
  if (process.env.SSH_TTY && !process.env.DISPLAY) {
    return true;
  }
  // Docker/container often has no GUI
  if (fs.existsSync('/.dockerenv')) {
    return true;
  }
  return false;
}

/**
 * Spawn a new terminal window running a command
 */
export async function spawnTerminal(
  command: string,
  args: string[]
): Promise<{ pid: number; waitForExit: () => Promise<number> }> {
  const terminal = await findTerminal();

  if (!terminal) {
    throw new Error(
      'No terminal emulator found. Install gnome-terminal, konsole, xterm, or xfce4-terminal.'
    );
  }

  // Validate and sanitize all arguments to prevent injection
  const safeCommand = sanitizeArg(command);
  const safeArgs = args.map(arg => {
    // Config paths must be in temp directory
    if (arg.startsWith('--config=') || arg.startsWith('--config')) {
      const configPath = arg.includes('=') ? arg.split('=')[1] : args[args.indexOf(arg) + 1];
      if (configPath && !isValidPath(configPath, [os.tmpdir()])) {
        throw new Error('Config path must be in system temp directory');
      }
    }
    return sanitizeArg(arg);
  });

  // Build the full command
  let termArgs: string[];
  let proc;

  if (terminal.cmd === 'gnome-terminal') {
    // gnome-terminal with --wait: blocks until command finishes
    // Wrap in bash to ensure proper argument handling
    const fullCmd = [safeCommand, ...safeArgs].join(' ');
    termArgs = [...terminal.args, 'bash', '-c', fullCmd];
    proc = spawn(terminal.cmd, termArgs, {
      detached: true,
      stdio: 'ignore',
    });
  } else if (terminal.cmd === 'open') {
    // macOS - use osascript with proper escaping
    const script = `tell application "Terminal" to do script "${safeCommand} ${safeArgs.join(' ')}"`;
    proc = spawn('osascript', ['-e', script], {
      detached: true,
      stdio: 'ignore',
    });
  } else if (terminal.wrapInBash) {
    // Some terminals need the command wrapped in bash
    const fullCmd = [safeCommand, ...safeArgs].join(' ');
    termArgs = [...terminal.args, 'bash', '-c', fullCmd];
    proc = spawn(terminal.cmd, termArgs, {
      detached: true,
      stdio: 'ignore',
    });
  } else {
    // Other terminals: pass command and args directly
    termArgs = [...terminal.args, safeCommand, ...safeArgs];
    proc = spawn(terminal.cmd, termArgs, {
      detached: true,
      stdio: 'ignore',
    });
  }

  // Allow the terminal to run independently
  proc.unref();

  return {
    pid: proc.pid || 0,
    waitForExit: () =>
      new Promise((resolve) => {
        proc.on('close', (code) => resolve(code || 0));
        proc.on('error', () => resolve(1));
      }),
  };
}

/**
 * Wait for a signal file to exist
 */
export async function waitForSignalFile(
  signalPath: string,
  timeoutMs: number = 300000 // 5 minutes default
): Promise<{ success: boolean; error?: string }> {
  const startTime = Date.now();
  const pollInterval = 500; // Check every 500ms

  while (Date.now() - startTime < timeoutMs) {
    if (await fs.pathExists(signalPath)) {
      // Read signal file content
      try {
        const content = await fs.readFile(signalPath, 'utf8');
        const signal = JSON.parse(content);

        // Clean up signal file
        await fs.remove(signalPath);

        if (signal.success) {
          return { success: true };
        } else {
          return { success: false, error: signal.error || 'Wizard cancelled' };
        }
      } catch {
        // File exists but invalid - assume success
        await fs.remove(signalPath);
        return { success: true };
      }
    }

    // Wait before next poll
    await new Promise((resolve) => setTimeout(resolve, pollInterval));
  }

  return { success: false, error: 'Timeout waiting for wizard completion' };
}
